import { supabase } from '../lib/supabase';

// ============================================================
// ROLE KEYS
// ============================================================
export const ROLE_KEYS = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  SALES_USER: 'SALES_USER',
  SALES_MANAGER: 'SALES_MANAGER',
  PROJECT_MANAGER: 'PROJECT_MANAGER',
  APPROVER: 'APPROVER',
  FINANCE: 'FINANCE',
  HR_MASTER_DATA: 'HR_MASTER_DATA',
  EMPLOYEE: 'EMPLOYEE',
  AUDITOR: 'AUDITOR',
};

// ============================================================
// MODULE KEYS
// ============================================================
export const MODULE_KEYS = {
  SERVICE_CALCULATOR: 'service_calculator',
  SAVED_CALCULATIONS: 'saved_calculations',
  INCENTIVE_TICKETS: 'incentive_tickets',
  TEAM_ASSIGNMENT: 'team_assignment',
  PENDING_APPROVAL: 'pending_approval',
  FINANCE_PAYMENTS: 'finance_payments',
  FINANCE_PAYMENT_REPORT: 'finance_payment_report',
  EMPLOYEE_INCENTIVE_REPORTS: 'employee_incentive_reports',
  ANALYTICS_DASHBOARD: 'analytics_dashboard',
  MASTER_DATA: 'master_data',
  TENDER_MANAGEMENT: 'tender_management',
  USERS: 'users',
  SETTINGS: 'settings',
  ROLES_PERMISSIONS: 'roles_permissions',
};

// ============================================================
// PERMISSION KEYS
// ============================================================
export const PERM_KEYS = {
  VIEW: 'view',
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  SUBMIT: 'submit',
  APPROVE: 'approve',
  REJECT: 'reject',
  HOLD: 'hold',
  RETURN_FOR_EDIT: 'return_for_edit',
  MARK_CLOSE_WON: 'mark_close_won',
  CREATE_INCENTIVE_TICKET: 'create_incentive_ticket',
  ADD_TEAM_MEMBER: 'add_team_member',
  REMOVE_TEAM_MEMBER: 'remove_team_member',
  VALIDATE_CALCULATE: 'validate_calculate',
  MARK_READY_FOR_PAYMENT: 'mark_ready_for_payment',
  MARK_PAID: 'mark_paid',
  EXPORT_PDF: 'export_pdf',
  EXPORT_EXCEL: 'export_excel',
  PRINT: 'print',
  MANAGE_USERS: 'manage_users',
  MANAGE_ROLES: 'manage_roles',
  MANAGE_SETTINGS: 'manage_settings',
  VIEW_ALL: 'view_all',
  VIEW_OWN: 'view_own',
  VIEW_TEAM: 'view_team',
  VIEW_FINANCE_ONLY: 'view_finance_only',
  WORKFLOW_ROLLBACK: 'workflow_rollback',
};

// ============================================================
// TICKET STATUS VISIBILITY BY ROLE
// ============================================================
export const ROLE_TICKET_STATUSES = {
  SUPER_ADMIN: null, // null = all
  ADMIN: null,
  PROJECT_MANAGER: ['Pending Team Assignment', 'Calculated', 'Returned for Edit'],
  APPROVER: ['Pending Approval', 'Approved', 'Rejected', 'On Hold'],
  FINANCE: ['Approved', 'Ready for Payment', 'Paid'],
  SALES_USER: null, // filtered by owner
  SALES_MANAGER: null, // filtered by team
  EMPLOYEE: null, // filtered by employee_id
  HR_MASTER_DATA: null,
  AUDITOR: null,
};

// ============================================================
// FETCH: all roles
// ============================================================
export const fetchRoles = async () => {
  const { data, error } = await supabase
    ?.from('roles')
    ?.select('*')
    ?.order('role_name');
  if (error) throw error;
  return data || [];
};

// ============================================================
// FETCH: all permissions (grouped by module)
// ============================================================
export const fetchPermissions = async () => {
  const { data, error } = await supabase
    ?.from('permissions')
    ?.select('*')
    ?.order('module_key')
    ?.order('permission_key');
  if (error) throw error;
  return data || [];
};

// ============================================================
// FETCH: role_permissions for a specific role
// ============================================================
export const fetchRolePermissions = async (roleId) => {
  const { data, error } = await supabase
    ?.from('role_permissions')
    ?.select('*, permissions(*)')
    ?.eq('role_id', roleId)
    ?.eq('is_allowed', true);
  if (error) throw error;
  return data || [];
};

// ============================================================
// FETCH: all role_permissions (for matrix view)
// ============================================================
export const fetchAllRolePermissions = async () => {
  const { data, error } = await supabase
    ?.from('role_permissions')
    ?.select('role_id, permission_id, is_allowed');
  if (error) throw error;
  return data || [];
};

// ============================================================
// UPSERT: role_permissions for a role (full matrix save)
// ============================================================
export const saveRolePermissions = async (roleId, permissionIds, allPermissionIds) => {
  if (!roleId || !allPermissionIds?.length) return;

  // Build upsert rows: allowed for checked, not allowed for unchecked
  const rows = allPermissionIds?.map(permId => ({
    role_id: roleId,
    permission_id: permId,
    is_allowed: permissionIds?.includes(permId),
    updated_at: new Date()?.toISOString(),
  }));

  // Primary approach: upsert using the unique constraint
  const { error: upsertError } = await supabase
    ?.from('role_permissions')
    ?.upsert(rows, { onConflict: 'role_id,permission_id', ignoreDuplicates: false });

  if (!upsertError) return;

  // Fallback approach: delete existing rows for this role, then insert fresh
  console.warn('[RBAC] Upsert failed, using delete+insert fallback:', upsertError?.message);

  const { error: deleteError } = await supabase
    ?.from('role_permissions')
    ?.delete()
    ?.eq('role_id', roleId);

  if (deleteError) throw deleteError;

  // Insert in batches of 100 to avoid request size limits
  const batchSize = 100;
  for (let i = 0; i < rows?.length; i += batchSize) {
    const batch = rows?.slice(i, i + batchSize);
    const { error: insertError } = await supabase
      ?.from('role_permissions')
      ?.insert(batch);
    if (insertError) throw insertError;
  }
};

// ============================================================
// CREATE ROLE
// ============================================================
export const createRole = async ({ roleKey, roleName, description }) => {
  const { data, error } = await supabase
    ?.from('roles')
    ?.insert({ role_key: roleKey, role_name: roleName, description, is_system_role: false })
    ?.select()
    ?.single();
  if (error) throw error;
  return data;
};

// ============================================================
// UPDATE ROLE
// ============================================================
export const updateRole = async (roleId, { roleName, description }) => {
  const { data, error } = await supabase
    ?.from('roles')
    ?.update({ role_name: roleName, description })
    ?.eq('id', roleId)
    ?.select()
    ?.single();
  if (error) throw error;
  return data;
};

// ============================================================
// DELETE ROLE (non-system only)
// ============================================================
export const deleteRole = async (roleId) => {
  const { error } = await supabase
    ?.from('roles')
    ?.delete()
    ?.eq('id', roleId)
    ?.eq('is_system_role', false);
  if (error) throw error;
};

// ============================================================
// FETCH: users with their roles
// ============================================================
export const fetchUsersWithRoles = async () => {
  const { data: profiles, error } = await supabase
    ?.from('user_profiles')
    ?.select('*')
    ?.order('created_at', { ascending: false });
  if (error) throw error;

  // Fetch user_roles
  const { data: userRoles } = await supabase
    ?.from('user_roles')
    ?.select('user_id, role_id, roles(role_key, role_name)');

  const userRolesMap = {};
  userRoles?.forEach(ur => {
    if (!userRolesMap?.[ur?.user_id]) userRolesMap[ur?.user_id] = [];
    userRolesMap?.[ur?.user_id]?.push(ur?.roles);
  });

  // Fetch user_employee_links
  const { data: empLinks } = await supabase
    ?.from('user_employee_links')
    ?.select('user_id, employee_id, employees(id, full_name, employee_code, department, role)');

  const empLinksMap = {};
  empLinks?.forEach(el => { empLinksMap[el?.user_id] = el?.employees; });

  return (profiles || [])?.map(p => ({
    ...p,
    rbacRoles: userRolesMap?.[p?.id] || [],
    linkedEmployee: empLinksMap?.[p?.id] || null,
  }));
};

// ============================================================
// ASSIGN ROLE TO USER
// ============================================================
export const assignUserRole = async (userId, roleId) => {
  const { error } = await supabase
    ?.from('user_roles')
    ?.upsert({ user_id: userId, role_id: roleId }, { onConflict: 'user_id,role_id' });
  if (error) throw error;
};

// ============================================================
// REMOVE ROLE FROM USER
// ============================================================
export const removeUserRole = async (userId, roleId) => {
  const { error } = await supabase
    ?.from('user_roles')
    ?.delete()
    ?.eq('user_id', userId)
    ?.eq('role_id', roleId);
  if (error) throw error;
};

// ============================================================
// LINK USER TO EMPLOYEE
// ============================================================
export const linkUserToEmployee = async (userId, employeeId) => {
  const { error } = await supabase
    ?.from('user_employee_links')
    ?.upsert({ user_id: userId, employee_id: employeeId }, { onConflict: 'user_id' });
  if (error) throw error;
};

// ============================================================
// UNLINK USER FROM EMPLOYEE
// ============================================================
export const unlinkUserFromEmployee = async (userId) => {
  const { error } = await supabase
    ?.from('user_employee_links')
    ?.delete()
    ?.eq('user_id', userId);
  if (error) throw error;
};

// ============================================================
// FETCH: current user's RBAC roles + permissions
// ============================================================
export const fetchMyRbacData = async (userId) => {
  if (!userId) return { roles: [], permissions: {}, employeeId: null };

  try {
    // Get user's roles
    const { data: userRoles } = await supabase
      ?.from('user_roles')
      ?.select('role_id, roles(id, role_key, role_name)')
      ?.eq('user_id', userId);

    const roles = userRoles?.map(ur => ur?.roles)?.filter(Boolean) || [];
    const roleIds = roles?.map(r => r?.id);

    // Get permissions for those roles
    let permMap = {};
    if (roleIds?.length > 0) {
      const { data: rolePerms } = await supabase
        ?.from('role_permissions')
        ?.select('permission_id, is_allowed, permissions(module_key, permission_key)')
        ?.in('role_id', roleIds)
        ?.eq('is_allowed', true);

      rolePerms?.forEach(rp => {
        const mod = rp?.permissions?.module_key;
        const perm = rp?.permissions?.permission_key;
        if (mod && perm) {
          if (!permMap?.[mod]) permMap[mod] = {};
          permMap[mod][perm] = true;
        }
      });
    }

    // Get linked employee
    const { data: empLink } = await supabase
      ?.from('user_employee_links')
      ?.select('employee_id')
      ?.eq('user_id', userId)
      ?.single();

    return {
      roles,
      roleKeys: roles?.map(r => r?.role_key),
      permissions: permMap,
      employeeId: empLink?.employee_id || null,
    };
  } catch (err) {
    console.warn('[RBAC] Failed to fetch RBAC data:', err?.message);
    return { roles: [], roleKeys: [], permissions: {}, employeeId: null };
  }
};

// ============================================================
// FETCH: employee pool (uses existing 'employees' table)
// ============================================================
export const fetchEmployeePool = async () => {
  const { data, error } = await supabase
    ?.from('employees')
    ?.select('*')
    ?.eq('is_active', true)
    ?.order('full_name');
  if (error) throw error;
  return data || [];
};

// ============================================================
// CREATE / UPDATE / DEACTIVATE employee
// ============================================================
export const createEmployee = async (emp) => {
  const { data, error } = await supabase
    ?.from('employees')
    ?.insert(emp)
    ?.select()
    ?.single();
  if (error) throw error;
  return data;
};

export const updateEmployee = async (id, updates) => {
  const { data, error } = await supabase
    ?.from('employees')
    ?.update({ ...updates, updated_at: new Date()?.toISOString() })
    ?.eq('id', id)
    ?.select()
    ?.single();
  if (error) throw error;
  return data;
};

export const deactivateEmployee = async (id) => {
  return updateEmployee(id, { is_active: false, status: 'inactive' });
};
